/**
 * LocationComparisonMode
 * Side-by-side comparison of 2-3 QSR locations across all key metrics.
 * Highlights best/worst per metric with visual bar charts.
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  BarChart3, MapPin, Trophy, AlertTriangle, TrendingUp, TrendingDown,
  DollarSign, Users, Clock, Car, ChevronRight, Minus, ArrowRight, X
} from 'lucide-react';
import { QSR_DEMO_LOCATIONS } from '@/config/qsrConfig';

const METRICS = [
  { key: 'splh', label: 'SPLH', format: v => `$${v.toFixed(2)}`, getter: l => l.metrics.splh, higherBetter: true, unit: '$/hr' },
  { key: 'dailyRevenue', label: 'Daily Revenue', format: v => `$${v.toLocaleString()}`, getter: l => l.metrics.dailyRevenue, higherBetter: true, unit: '$' },
  { key: 'monthlyRevenue', label: 'Monthly Revenue', format: v => `$${(v/1000).toFixed(0)}K`, getter: l => l.metrics.monthlyRevenue, higherBetter: true, unit: '$' },
  { key: 'labourCost', label: 'Labour Cost %', format: v => `${v.toFixed(1)}%`, getter: l => l.metrics.labourCostPercent, higherBetter: false, unit: '%', target: 25 },
  { key: 'foodCost', label: 'Food Cost %', format: v => `${v.toFixed(1)}%`, getter: l => l.metrics.foodCostPercent, higherBetter: false, unit: '%', target: 31 },
  { key: 'avgTransaction', label: 'Avg Transaction', format: v => `$${v.toFixed(2)}`, getter: l => l.metrics.avgTransactionValue, higherBetter: true, unit: '$' },
  { key: 'transactions', label: 'Transactions/Day', format: v => v.toLocaleString(), getter: l => l.metrics.transactionsPerDay, higherBetter: true, unit: '' },
  { key: 'staffOnShift', label: 'Staff On Shift', format: v => v.toString(), getter: l => l.metrics.staff.onShift, higherBetter: null, unit: '' },
  { key: 'growthPct', label: 'Revenue Growth', format: v => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`, getter: l => l.performance.changePercent, higherBetter: true, unit: '%' },
  { key: 'rating', label: 'Customer Rating', format: v => v.toFixed(1), getter: l => l.performance.rating, higherBetter: true, unit: '/5' },
];

const DT_METRICS = [
  { key: 'dtWait', label: 'DT Avg Wait', format: v => `${v.toFixed(1)} min`, getter: l => l.metrics.driveThrough?.avgWaitTime, higherBetter: false, unit: 'min' },
  { key: 'dtAccuracy', label: 'DT Accuracy', format: v => `${v.toFixed(1)}%`, getter: l => l.metrics.driveThrough?.accuracy, higherBetter: true, unit: '%' },
  { key: 'dtThroughput', label: 'DT Peak Cars/hr', format: v => v.toString(), getter: l => l.metrics.driveThrough?.peakThroughput, higherBetter: true, unit: '' },
];

function MetricBar({ value, min, max, isBest, isWorst, higherBetter }) {
  const range = max - min || 1;
  const pct = Math.max(5, Math.min(100, ((value - min) / range) * 100));
  const color = isBest
    ? 'bg-emerald-500'
    : isWorst
      ? 'bg-red-400'
      : 'bg-slate-300';
  return (
    <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export const LocationComparisonMode = () => {
  const [selected, setSelected] = useState([]);
  const [showPicker, setShowPicker] = useState(true);

  const toggle = (locId) => {
    setSelected(prev => {
      if (prev.includes(locId)) return prev.filter(id => id !== locId);
      if (prev.length >= 3) return prev;
      return [...prev, locId];
    });
  };

  const selectedLocations = useMemo(
    () => QSR_DEMO_LOCATIONS.filter(l => selected.includes(l.id)),
    [selected]
  );

  const allMetrics = useMemo(() => {
    const hasDT = selectedLocations.some(l => l.metrics.driveThrough);
    return hasDT ? [...METRICS, ...DT_METRICS] : METRICS;
  }, [selectedLocations]);

  const metricStats = useMemo(() => {
    if (selectedLocations.length < 2) return {};
    const stats = {};
    allMetrics.forEach(m => {
      const values = selectedLocations.map(l => ({ id: l.id, val: m.getter(l) })).filter(v => v.val != null);
      if (values.length < 2) { stats[m.key] = null; return; }
      const nums = values.map(v => v.val);
      const min = Math.min(...nums);
      const max = Math.max(...nums);
      const bestId = m.higherBetter === true
        ? values.find(v => v.val === max)?.id
        : m.higherBetter === false
          ? values.find(v => v.val === min)?.id
          : null;
      const worstId = m.higherBetter === true
        ? values.find(v => v.val === min)?.id
        : m.higherBetter === false
          ? values.find(v => v.val === max)?.id
          : null;
      stats[m.key] = { min, max, bestId, worstId };
    });
    return stats;
  }, [selectedLocations, allMetrics]);

  const winsCount = useMemo(() => {
    const counts = {};
    selectedLocations.forEach(l => { counts[l.id] = 0; });
    Object.values(metricStats).forEach(s => {
      if (s?.bestId) counts[s.bestId] = (counts[s.bestId] || 0) + 1;
    });
    return counts;
  }, [metricStats, selectedLocations]);

  if (showPicker) {
    return (
      <Card className="glass-card" data-testid="location-comparison-picker">
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-500" />
            Compare Locations
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Select 2 or 3 locations to compare side-by-side</p>
        </CardHeader>
        <CardContent>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {QSR_DEMO_LOCATIONS.map(loc => {
              const isSelected = selected.includes(loc.id);
              return (
                <div
                  key={loc.id}
                  role="button"
                  tabIndex={0}
                  data-testid={`compare-select-${loc.id}`}
                  onClick={() => toggle(loc.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(loc.id); } }}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left cursor-pointer ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <Checkbox checked={isSelected} className="pointer-events-none" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{loc.shortName}</p>
                    <p className="text-xs text-muted-foreground">{loc.state} &middot; {loc.type === 'drive_through' ? 'Drive-Through' : 'Dine-In'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-900">${loc.metrics.splh.toFixed(0)}</p>
                    <p className="text-[10px] text-muted-foreground">SPLH</p>
                  </div>
                </div>
              );
            })}
          </div>
          {selected.length >= 2 && (
            <Button
              className="mt-4 w-full"
              onClick={() => setShowPicker(false)}
              data-testid="compare-start-btn"
            >
              Compare {selected.length} Locations <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {selected.length === 1 && (
            <p className="text-xs text-muted-foreground text-center mt-3">Select at least one more location</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card" data-testid="location-comparison-results">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-500" />
            Location Comparison
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => { setShowPicker(true); }} data-testid="compare-change-btn">
            Change <ChevronRight className="w-4 h-4 ml-0.5" />
          </Button>
        </div>

        {/* Location column headers with win badges */}
        <div className="grid gap-2 mt-3" style={{ gridTemplateColumns: `140px repeat(${selectedLocations.length}, 1fr)` }}>
          <div />
          {selectedLocations.map(loc => (
            <div key={loc.id} className="text-center" data-testid={`compare-header-${loc.id}`}>
              <div className="flex items-center justify-center gap-1">
                <MapPin className="w-3 h-3 text-slate-400" />
                <span className="text-sm font-semibold">{loc.shortName}</span>
                <Badge variant="outline" className="text-[9px] px-1">{loc.state}</Badge>
              </div>
              <div className="flex items-center justify-center gap-1 mt-1">
                <Trophy className="w-3 h-3 text-amber-500" />
                <span className="text-[10px] text-amber-600 font-medium">{winsCount[loc.id] || 0} wins</span>
              </div>
            </div>
          ))}
        </div>
      </CardHeader>

      <CardContent className="space-y-1 pt-0">
        {allMetrics.map(m => {
          const stats = metricStats[m.key];
          if (!stats) return null;
          return (
            <div
              key={m.key}
              className="grid gap-2 py-2 border-b border-slate-50 last:border-0 items-center"
              style={{ gridTemplateColumns: `140px repeat(${selectedLocations.length}, 1fr)` }}
              data-testid={`compare-row-${m.key}`}
            >
              <div className="text-xs text-slate-500 font-medium pr-2">{m.label}</div>
              {selectedLocations.map(loc => {
                const val = m.getter(loc);
                if (val == null) return <div key={loc.id} className="text-center text-xs text-slate-300">N/A</div>;
                const isBest = stats.bestId === loc.id;
                const isWorst = stats.worstId === loc.id;
                return (
                  <div key={loc.id} className="space-y-1" data-testid={`compare-cell-${m.key}-${loc.id}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-semibold ${isBest ? 'text-emerald-600' : isWorst ? 'text-red-500' : 'text-slate-800'}`}>
                        {m.format(val)}
                      </span>
                      {isBest && <Trophy className="w-3 h-3 text-amber-500" />}
                      {isWorst && <AlertTriangle className="w-3 h-3 text-red-400" />}
                    </div>
                    <MetricBar value={val} min={stats.min} max={stats.max} isBest={isBest} isWorst={isWorst} higherBetter={m.higherBetter} />
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Summary insight */}
        <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-3 mt-3" data-testid="compare-summary">
          <p className="text-xs text-indigo-700 font-medium flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" />
            Quick Insight
          </p>
          <p className="text-xs text-indigo-600 mt-1">
            {(() => {
              const best = selectedLocations.reduce((a, b) => (winsCount[a.id] || 0) >= (winsCount[b.id] || 0) ? a : b);
              const worst = selectedLocations.reduce((a, b) => (winsCount[a.id] || 0) <= (winsCount[b.id] || 0) ? a : b);
              return `${best.shortName} leads with ${winsCount[best.id]} metric wins. ${worst.shortName} has room for improvement — focus on ${worst.metrics.labourCostPercent > 26 ? 'labour cost optimisation' : worst.metrics.splh < 60 ? 'SPLH improvement' : 'operational consistency'} to close the gap.`;
            })()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default LocationComparisonMode;
